import BaseHelper from './BaseHelper'
import { wrapSvgText, ellipseSvgText } from '../utils/textUtils'

import { select as d3_select } from 'd3-selection'
import { max as d3_max, min as d3_min, extent as d3_extent } from 'd3-array'
import { zoom as d3_zoom, zoomIdentity as d3_zoomIdentity } from 'd3-zoom'
import { interpolateRound as d3_interpolateRound } from 'd3-interpolate'
import { scaleLinear, scaleSymlog, scalePow, scaleBand, scalePoint, scaleTime } from 'd3-scale'
import { axisBottom, axisTop, axisLeft, axisRight } from '../utils/custom-d3-axis/index.js'
import chunky from '../utils/chunkyScale'
import outliers from '../utils/outliers'

const d3Scales = { scaleLinear, scaleSymlog, scaleBand, scalePow, scalePoint, scaleTime, chunky }
const d3Axes = { axisBottom, axisTop, axisLeft, axisRight }

const DEFAULT_TICK_COUNT = { x: 5, y: 8 }

export const GAP_SIZE = 20

export function categoricalExtent(data, getter = d => d.id) {
  if (data[0] && Array.isArray(data[0].value)) {
    let allValues = data.map(row => row.value.map(getter))
    allValues = allValues.reduce((acc, val) => acc.concat(val), [])
    return allValues.filter((d, i) => allValues.indexOf(d) === i)
  } else {
    return data.map(getter)
  }
}

export function continuousExtentParts(data, getter = d => d.value) {
  // return d3_extent(data.reduce((res,d)=>res.concat(d3_extent(d.value,(dd)=>getter(dd))),[]))
  let dict = data.reduce((res, d) => {
    d.value.forEach(dd => {
      if (!res[dd.id]) res[dd.id] = [Infinity, -Infinity]
      let values = getter(dd)
      if (!Array.isArray(values)) values = [values]

      let minV = d3_min(values)
      let maxV = d3_max(values)
      res[dd.id][0] = Math.min(res[dd.id][0], minV || minV === 0 ? minV : null)
      res[dd.id][1] = Math.max(res[dd.id][1], maxV || maxV === 0 ? maxV : null)
    })
    return res
  }, {})
  return Object.keys(dict).map(key => ({ value: dict[key].map(dd => ({ value: dd })) }))
}

export function continuousExtent(data, getter = d => d.value) {
  if (data[0] && Array.isArray(data[0].value)) {
    return d3_extent(data.reduce((res, d) => res.concat(d3_extent(d.value, dd => getter(dd))), []))
  } else {
    return d3_extent(data, getter)
  }
}

export function continuousExtentTicks(data, getter = d => d.value) {
  let ticks
  if (data[0] && Array.isArray(data[0].value)) {
    ticks = data.reduce((res, d) => res.concat(d.value.map(getter)), [])
  } else {
    ticks = data.map(getter)
  }
  return ticks.filter((d, i) => ticks.findIndex(dd => String(dd) === String(d)) === i)
}

export function cleanCollisions(axis, extent, extentParts, multiplier = 1.1) {
  if (!extentParts[0] || extent[0] == extent[1]) return extent
  let checkColision = ticks => {
    // return axis.type==='left' ? ticks[ticks.length-1]<extentParts[0].value[1].value*1.02 : ticks[ticks.length-1]<extentParts[extentParts.length-1].value[1].value*1.02
    let partToCheck =
      axis.type === 'left'
        ? extentParts[0].value[1].value
        : extentParts[extentParts.length - 1].value[1].value
    // console.log("checking",ticks[ticks.length-1],partToCheck)
    return ticks[ticks.length - 1] < partToCheck * multiplier
  }
  let tmpScale = axis.scale.copy().domain(extent)
  let ticks = tmpScale.ticks(DEFAULT_TICK_COUNT[axis[0]])
  let tickDist = Math.abs(ticks[1] - ticks[0])

  while (checkColision(ticks)) {
    // console.log("ticks",ticks)
    extent[1] = +extent[1] + tickDist
    // if(originalExtent[0]>=0) extent[0]=0
    tmpScale.domain(extent)
    ticks = tmpScale.ticks(DEFAULT_TICK_COUNT[axis[0]])
    tickDist = Math.abs(ticks[1] - ticks[0])
  }
  return extent
}
export function padLinear([x0, x1], k) {
  x0 = Number(x0)
  x1 = Number(x1)
  const d = x1 - x0 > 0 ? x1 - x0 : x1 * 0.2
  const dx = (d * k) / 2
  if (x0.getDate) return [new Date(x0 - dx), new Date(x1 + dx)]
  else return [x0 - dx, x1 + dx]
}
export function getMinDist(data, scale, getter) {
  if (Array.isArray(data[0].value)) {
    return data.reduce((min, row) => {
      return row.value.reduce((min, p, i) => {
        if (i > 0)
          min = Math.min(min, scale(getter(row.value[i])) - scale(getter(row.value[i - 1])))
        return min
      }, Infinity)
    }, Infinity)
  } else {
    return data.reduce((min, p, i) => {
      if (i > 0) min = Math.min(min, scale(getter(data[i])) - scale(getter(data[i - 1])))
      return min
    }, Infinity)
  }
}
export default class AxisHelper extends BaseHelper {
  static _firstAxisRender = true

  static configName = 'AxisHelper'

  static defaultConfig = {
    index: 102,
    x: {
      /**
       * Scale to use in the X dimension. Use Logarithmic or Power if your dataset spans multiple orders of magnitude.
       */
      scale: {
        type: String,
        default: 'linear',
        options: ['linear', 'log', 'pow', 'band', 'point'],
        exposed: true,
      },
      side: {
        type: String,
        default: 'bottom',
        options: ['bottom', 'top'],
        exposed: false,
        nonEditable: true,
      },
      domain: {
        type: Array,
        default: ['auto', 'auto'],
      },
      getExtent: {
        type: Function,
        default: null,
      },
      getDataType: {
        type: [Function, null],
        default: null,
      },
      getValue: {
        type: [Function, null],
        default: null,
      },
      dimensions: {
        type: [String, null],
        default: 'single',
      },
      /**
       * Show/hide equispaced values on the X axis.
       */
      showTickValues: {
        type: Boolean,
        default: true,
        exposed: true,
      },
      /**
       * Show/hide vertical lines.
       */
      grid: {
        type: Boolean,
        default: false,
        exposed: true,
      },
      /**
       * Text format for the values on the X axis, and the tooltips. See [valueFormat](#/developer/PropDetails#valueFormat)
       */
      valueFormat: {
        type: [String, Array, Function, null],
        default: null,
        exposed: true,
      },
      /**
       * Unit to be appended to the formatted values. ('$', '¤', '£' will be prepended instead)
       */
      unit: {
        type: [String, Array, null],
        default: null,
        exposed: true,
      },
      /**
       * Orientation of the values on the X axis.
       */
      tickOrientation: {
        type: [String],
        default: 'horizontal',
        options: ['horizontal', 'vertical', 'skewed'],
        exposed: true,
      },
      /**
       * Text wrap of the values on the X axis.
       */
      tickWrap: {
        type: [String],
        default: 'wrap',
        options: ['wrap', 'ellipsis', 'none'],
        exposed: true,
      },
      /**
       * Force the axis to have a tick on each data point.
       */
      tickMatchData: {
        type: [Boolean],
        default: true,
        exposed: false,
      },
      /**
       * Value on which to slice the range of the X axis. Usefull when the dataset has some outliers.
       */
      trimThreshold: {
        type: [Boolean, Number, String],
        default: false,
        exposed: true,
      },
      customWrapMax: {
        type: [null, Number],
        default: null,
        exposed: true,
      },
    },
    y: {
      /**
       * Scale to use in the Y dimension. Use Logarithmic or Power if your dataset spans multiple orders of magnitude.
       */
      scale: {
        type: [String, Array],
        default: 'linear',
        options: ['linear', 'log', 'pow', 'band', 'point'],
        exposed: true,
      },
      /**
       * Side on which to draw the Y axis.
       */
      side: {
        type: String,
        default: 'left',
        options: ['left', 'right'],
        exposed: true,
      },
      domain: {
        type: Array,
        default: ['auto', 'auto'],
      },
      getExtent: {
        type: Function,
        default: null,
      },
      getDataType: {
        type: [Function, null],
        default: null,
      },
      getValue: {
        type: [Function, null],
        default: null,
      },
      dimensions: {
        type: String,
        default: 'single',
      },
      /**
       * Show/hide equispaced values on the Y axis.
       */
      showTickValues: {
        type: Boolean,
        default: true,
        exposed: true,
      },
      /**
       * Show/hide horizontal lines.
       */
      grid: {
        type: Boolean,
        default: true,
        exposed: true,
      },
      /**
       * Text format for the values on the Y axis, and the tooltips. See [valueFormat](#/developer/PropDetails#valueFormat)
       */
      valueFormat: {
        type: [String, Array, Function, null],
        default: null,
        exposed: true,
      },
      /**
       * Unit to be appended to the formatted values. ('$', '¤', '£' will be prepended instead)
       */
      unit: {
        type: [String, Array, null],
        default: null,
        exposed: true,
      },
      /**
       * Orientation of the values on the Y axis.
       */
      tickOrientation: {
        type: [String, Array],
        default: 'horizontal',
        options: ['horizontal', 'vertical', 'skewed'],
        exposed: true,
      },
      /**
       * Text wrap of the values on the Y axis.
       */
      tickWrap: {
        type: [String],
        default: 'wrap',
        options: ['wrap', 'ellipsis', 'none'],
        exposed: true,
      },
      /**
       * Force the axis to have a tick on each data point.
       */
      tickMatchData: {
        type: [Boolean],
        default: false,
        exposed: false,
      },
      /**
       * Value on which to slice the range of the Y axis. Usefull when the dataset has some outliers.
       */
      trimThreshold: {
        type: [Boolean, Number, String],
        default: false,
        exposed: true,
      },
    },
    /**
     * Default zoom explanation (should be overridden)
     */
    zoom: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    zoomAxes: {
      type: Array,
      default: ['x', 'y'],
      exposed: false,
    },
    fontSize: {
      type: Number,
      default: 14,
      exposed: true,
    },
    margin: {
      type: Object,
      default: { top: 40, right: 20, left: 20, bottom: 70 },
      exposed: true,
    },
    // semanticZoom: {
    //   type: Boolean,
    //   default: false
    // },
    // geometricZoom: {
    //   type: Boolean,
    //   default: false
    // }
  }
  static init() {}

  static getYAxesIds = function() {
    if (this.config.AxisHelper.y.dimensions === 'data.dimensions')
      return this.data[0] ? Object.keys(this.data[0].dimensions).map((d, i) => 'y_' + i) : []
    else if (this.config.AxisHelper.y.dimensions === 'line-and-bar') return ['y_0', 'y_1']
    else return ['y']
    // return this.config.AxisHelper.y.multi?Object.keys(this.data[0].dimensions).map((d,i)=>'y_'+i):['y']
  }
  static getYAxesLabels = function() {
    if (this.config.AxisHelper.y.dimensions === 'data.dimensions')
      return this.data[0] ? Object.keys(this.data[0].dimensions).map(d => d) : []
    else if (this.config.AxisHelper.y.dimensions === 'line-and-bar') return ['y_lines', 'y_bars']
    else return ['y-label?']
    // return this.config.AxisHelper.y.multi?Object.keys(this.data[0].dimensions).map((d)=>d):['y-label?']
  }
  static getXAxesIds = function() {
    if (this.config.AxisHelper.x.dimensions === null) return []
    else return ['x']
    // return this.config.AxisHelper.y.multi?Object.keys(this.data[0].dimensions).map((d,i)=>'y_'+i):['y']
  }
  static getXAxesLabels = function() {
    if (this.config.AxisHelper.x.dimensions === null) return []
    else return ['x-label?']
  }
  static update() {
    let yDims = this.getYAxesIds()
    let xDims = this.getXAxesIds()
    this.axes = {
      ...xDims.reduce((o, dim) => {
        return { ...o, [dim]: {} }
      }, {}),
      ...yDims.reduce((o, dim) => {
        return { ...o, [dim]: {} }
      }, {}),
    }

    this.defMargin = this.config.AxisHelper.margin
    this.defMargin[this.config.AxisHelper.y.side] = this.config.isMobile
      ? this.config.AxisHelper.fontSize < 12
        ? 20
        : 40
      : this.config.AxisHelper.fontSize > 12
      ? 40
      : 60
    if (yDims.length > 1) {
      this.defMargin.left = 40
      this.defMargin.right = 40
    }
    this.margin = { ...this.defMargin }

    this.contentSize ||= { width: 0, height: 0 }

    xDims.forEach(dim => this._initAxis(dim))
    yDims.forEach(dim => this._initAxis(dim))
  }

  static _getAxisOrder = function() {
    let yDims = this.getYAxesIds()
    let xDims = this.getXAxesIds()
    if (this.config.horizontal) return [...yDims, ...xDims]
    else return [...xDims, ...yDims]
  }

  static getd3Scale = function(type, axis) {
    let computedThreshold
    if (this.config.AxisHelper[axis[0]].trimThreshold === true) {
      let min = Math.min(...outliers(this.data.map(d => d.value)))
      computedThreshold = min !== Infinity ? min : null
    } else {
      computedThreshold = this.config.AxisHelper[axis[0]].trimThreshold
    }
    let d3Type
    let dataType =
      this.config.AxisHelper[axis[0]].getDataType &&
      this.config.AxisHelper[axis[0]].getDataType(this.data)
    if (dataType === String && type === 'linear') {
      d3Type = 'scalePoint'
    } else if (dataType === Date && type === 'linear') {
      d3Type = 'scaleTime'
    } else if (type === 'linear' && computedThreshold) {
      d3Type = 'chunky'
    } else if (type === 'log') {
      d3Type = 'scaleSymlog'
    } else {
      d3Type = 'scale' + type.charAt(0).toUpperCase() + type.slice(1)
    }
    let scale = d3Scales[d3Type]()
    scale.type = d3Type
    if (computedThreshold) scale._threshold = computedThreshold
    return scale
  }

  //BULLSHIT TOTAL... pero weno..
  static getTickCount = function(axis, values) {
    if (axis === 'x') return DEFAULT_TICK_COUNT['y']
    let contentSize = {}
    contentSize.width = this.size.width - this.margin.left - this.margin.right
    contentSize.height = this.size.height - this.margin.top - this.margin.bottom
    let domain = this._getExtent(axis)
    let width = axis === 'x' ? contentSize.width : contentSize.height
    let base = DEFAULT_TICK_COUNT[axis]
    if (domain[1] - domain[0] < base) base = Math.max(2, Math.ceil(domain[1] - domain[0]))
    if (width / base < 40) base = width / 40
    if (values && base > values.length) base = values.length
    return base
  }
  static _initAxis = function(axis) {
    let yDims = this.getYAxesIds()
    let xDims = this.getXAxesIds()
    let dims = { x: xDims, y: yDims }
    let scaleStr

    if (Array.isArray(this.config.AxisHelper[axis[0]].scale)) {
      scaleStr =
        this.config.AxisHelper[axis[0]].scale[dims[axis[0]].indexOf(axis)] ||
        this.config.AxisHelper[axis[0]].scale[0]
    } else {
      scaleStr = this.config.AxisHelper[axis[0]].scale
    }
    let scale = this.getd3Scale(scaleStr, axis)

    let axisType
    if (xDims.length > 0 && yDims.length > 0 && this._getAxisOrder().indexOf(axis) === 0) {
      if (
        (this.config.horizontal && this.config.AxisHelper[axis[0]].side === 'left') ||
        this.config.AxisHelper[axis[0]].side === 'top'
      )
        axisType = 'top'
      else axisType = 'bottom'
    } else {
      // axisType = (this.config.horizontal || this.config.AxisHelper[axis[0]].side==='left')?'axisLeft':'axisRight'//'axisLeft'
      if (yDims.length > 1 && yDims.indexOf(axis) === yDims.length - 1) axisType = 'right'
      else if (
        (this.config.horizontal && this.config.AxisHelper[axis[0]].side === 'bottom') ||
        this.config.AxisHelper[axis[0]].side === 'left'
      )
        axisType = 'left'
      else axisType = 'right'
    }
    let d3AxisType = 'axis' + axisType.charAt(0).toUpperCase() + axisType.slice(1)
    // this.axes[axis].func = d3[d3AxisType]()
    this.axes[axis].scale = scale
    this.axes[axis].type = axisType

    let custom_tick_count = this.getTickCount(axis[0])
    this.axes[axis].func = d3Axes[d3AxisType]()
      .scale(scale.copy())
      .ticks(custom_tick_count)

    // Unpoco costoso, y se hace cada vez que se pinta un tick...
    let shouldClean = (func, axis) => {
      let ticks =
        func.tickValues() == null
          ? func.scale().ticks
            ? func.scale().ticks.apply(func.scale(), func.tickArguments())
            : func.scale().domain()
          : func.tickValues()
      return ticks.every(t => {
        return (
          this.formatValue
            .bind(this)(t, axis)
            .match(/\.00([^0-9]*)$/) ||
          this.formatValue
            .bind(this)(t, axis)
            .match(/\.0([^0-9]*)$/)
        )
      })
    }

    this.axes[axis].func
      // .tickFormat(this.config.AxisHelper[axis[0]].tickFormat.bind(this))
      .tickFormat(d => {
        let str = this.formatValue.bind(this)(d, axis)
        if (shouldClean(this.axes[axis].func, axis))
          str = str.replace(/\.00([^0-9]*)$/, '$1').replace(/\.0([^0-9]*)$/, '$1')
        // if(this.axes[axis].func.shouldClean) str = str.replace(/\.00([^0-9]*)$/,'$1').replace(/\.0([^0-9]*)$/,'$1')
        return str
      })
      .tickSize(5)
      .tickSizeOuter(0) //solo le importa al parallelcorrds, y quiere un 0 :D
      .drawTicks((enter, update) => this._drawTicks(axis, enter, update))
  }

  static _setDomain = function(axis) {
    let domain
    domain = this._getExtent(axis)

    if (this.config.AxisHelper[axis[0]].domain[0] !== 'auto')
      domain[0] = this.config.AxisHelper[axis[0]].domain[0]
    if (this.config.AxisHelper[axis[0]].domain[1] !== 'auto')
      domain[1] = this.config.AxisHelper[axis[0]].domain[1]

    // "chapuzilla" para los datumvalus que no es salgan (sobretodo en horizontal!)
    if (axis[0] === 'y' && this.config.showDatumValues) {
      domain = [domain[0], padLinear(domain, 0.2)[1]]
    }
    // Idem para lines and bars
    if (
      (axis === 'y_0' && this.config.showLineDatumValues) ||
      (axis === 'y_1' && this.config.showBarDatumValues)
    ) {
      domain = [domain[0], padLinear(domain, 0.2)[1]]
    }
    // MMM no estoy nada convencido de este "if/else" pero lo del this.zeroBased no pinta mal...
    if (axis[0] === 'y' && this.zeroBased && domain[0] >= 0) domain[0] = 0
    // else if(!this.axes[axis].func.scale().isChunky && domain.length===2) domain = padLinear(domain,0.2)
    else if (
      !this.axes[axis].func.scale().isChunky &&
      this.config.AxisHelper[axis[0]].scale !== 'band' &&
      this.config.AxisHelper[axis[0]].scale !== 'point' &&
      this.axes[axis].scale.type !== 'scalePoint'
    ) {
      if (axis[0] === 'x' && this.config.AxisHelper.zoom) {
        domain = padLinear(domain, 0.07)
      } else {
        domain = padLinear(domain, 0.1)
      }
    }

    if (this.axes[axis].scale.isChunky) {
      let threshold = this.axes[axis].scale._threshold
      let max = domain[1]

      // OLD: let next = this.data.reduce((min,d)=>this._getValueOnAxis(d,axis)<th?min:Math.min(min,this._getValueOnAxis(d,axis)),max)
      let allValues = categoricalExtent(this.data, d => this._getValueOnAxis(d, axis))
      let next = allValues.reduce((min, d) => (d < threshold ? min : Math.min(min, d)), max)
      let ticks = d3Scales
        .scaleLinear()
        .domain([next, max])
        .ticks(2)

      // intentmaos forzar "en el tick"
      if (next > ticks[0]) next = ticks[0]
      else if (ticks.length > 1) next = ticks[0] - (ticks[1] - ticks[0])

      let dom0 = [domain[0], threshold]
      let dom1 = [next, max]
      dom0 = [padLinear(dom0, 0.2)[0], dom0[1]]
      dom1 = [dom1[0], padLinear(dom1, 0.2)[1]]

      domain = [dom0, dom1]
    }

    this.axes[axis].scale.domain(domain)
    this.axes[axis].func.scale().domain(domain)

    // PREUBA: solo en "x axis" si hay en los datos MENOS ticks que el "DEFAULT_TICK_COUNT" pintamos SOLO los ticks de los datos
    // PERO entonces, si no son "equiestpaciados" queda MUY raro...
    if (
      axis[0] === 'x' &&
      this.config.AxisHelper.y.dimensions !== 'line-and-bar' &&
      this.config.AxisHelper[axis[0]].scale !== 'band' &&
      this.config.AxisHelper[axis[0]].scale !== 'point'
    ) {
      // let custom_tick_count = this.getTickCount(axis[0])
      let uniqueTicks = continuousExtentTicks(this.data, d => d.x || d.id)
      if (
        // uniqueTicks.length < custom_tick_count ||
        this.config.AxisHelper[axis[0]].tickMatchData
      ) {
        // this.axes[axis].scale.tickValues(this.data.map(d => d.id))
        // this.axes[axis].func.scale().tickValues(this.data.map(d => d.id))
        this.axes[axis].func.tickValues(uniqueTicks)
      }
    }
  }
  static _getTickOrientationOnAxis = function(axis) {
    let type = axis[0].toUpperCase()
    let dimIds = this[`get${type}AxesIds`]() //this.getYAxesIds()
    if (Array.isArray(this.config.AxisHelper[axis[0]].tickOrientation)) {
      return this.config.AxisHelper[axis[0]].tickOrientation[dimIds.indexOf(axis)]
    } else {
      return this.config.AxisHelper[axis[0]].tickOrientation
    }
  }
  static _getValueOnAxis = function(d, axis) {
    if (this.config.AxisHelper[axis[0]].getValue) return this.config.AxisHelper[axis[0]].getValue(d)
    else return d.value
  }
  static _getExtent = function(axis) {
    let type = axis[0].toUpperCase()
    let dimNames = this[`get${type}AxesLabels`]() // ]this.getYAxesLabels()
    let dimIds = this[`get${type}AxesIds`]() //this.getYAxesIds()
    if (this.config.AxisHelper[axis[0]].getExtent)
      return this.config.AxisHelper[axis[0]].getExtent.call(
        this,
        this.data,
        dimNames[dimIds.indexOf(axis)],
        axis
      )
    else if (this.config.AxisHelper[axis[0]].scale === 'scaleBand') return this.data.map(d => d.id)
    else return d3_extent(this.data, d => d.value)
  }

  static _setRange = function(axis) {
    let range
    if (this.getXAxesIds().length > 0) {
      if (axis === this._getAxisOrder()[0]) range = [0, this.contentSize.width]
      else range = [this.contentSize.height, 0]
    } else {
      range = [0, Math.min(this.contentSize.width / 2, this.contentSize.height / 2) - 20]
    }

    // chapuzilla para el "parallels" pero parece OK, no?
    if (this.axes[axis].scale.bandwidth && this.axes[axis].scale.bandwidth() === 0) {
      range = [range[0] + 40, range[1] - 40]
    }

    // UN POCO FEO:  DEERIA SER UN "else" no!?!?!?!
    if (this.axes[axis].func.scale().isChunky) {
      let gapHeight = GAP_SIZE
      let secondRange = this.contentSize.height * 0.2 + gapHeight / 2
      if (this.axes[axis].type === 'left' || this.axes[axis].type === 'right')
        range = [
          [range[0], range[1] + secondRange],
          [range[1] + (secondRange - gapHeight), range[1]],
        ]
      else
        range = [
          [range[0], range[1] - secondRange],
          [range[1] - (secondRange - gapHeight), range[1]],
        ]
    }

    this.axes[axis].scale.range(range)
    this.axes[axis].func.scale().range(range)

    if (this.config.AxisHelper.zoom && this.axes[axis[0]] && this.axes[axis[0]].scale.bandwidth) {
      let minBarWidth = 40
      //chapuzilla "grouped bars"
      if (this.config.mode === 'grouped') minBarWidth = this.data.length * minBarWidth
      //chapuzilla "paralels"
      if (this.getYAxesIds().length > 2) minBarWidth = 120

      let currentBandWidth = this.axes[axis[0]].scale.bandwidth()
      //excepcion para el "pointScale" del parallels, qeu devuelve siempre bandWidth=0
      if (currentBandWidth === 0)
        currentBandWidth =
          (this.axes[axis[0]].scale.range()[1] - this.axes[axis[0]].scale.range()[0]) /
          this.axes[axis[0]].scale.domain().length

      if (currentBandWidth < minBarWidth) {
        let newRangeExtent = minBarWidth * this.axes[axis[0]].scale.domain().length

        if (range[0] < range[1]) range[1] = range[0] + newRangeExtent
        else range[0] = range[1] + newRangeExtent

        //re-set after the fact
        this.axes[axis].scale.range(range)
        this.axes[axis].func.scale().range(range)
      }
    }
  }
  static _updateAxes = function() {
    this.needsRedraw = false
    this.__canSkip = false
    this.contentSize.width = this.size.width - this.margin.left - this.margin.right
    this.contentSize.height = this.size.height - this.margin.top - this.margin.bottom

    let yDims = this.getYAxesIds()
    // let yDimNames = this.getYAxesLabels()
    let xDims = this.getXAxesIds()
    // let xDimNames = this.getXAxesLabels()
    let axes = [...xDims, ...yDims]
    axes.forEach(axis => {
      this._setDomain(axis)
      this._setRange(axis)
    })
    this._drawAxes(axes)

    this._positionAxes()

    if (this.needsRedraw) {
      this._beforeDraws[this._helperIds.indexOf('AxisHelper')].call(this)
    } else {
      this._firstAxisRender = false
    }

    this.__canSkip = true
  }
  static _positionAxes = function() {
    let ylocations
    let yDims = this.getYAxesIds()
    let xDims = this.getXAxesIds()
    let yDimNames = this.getYAxesLabels()
    if (yDims.length === 1 || xDims.length === 0) ylocations = () => 0
    else if (yDims.length === 2)
      ylocations = d => (yDims.indexOf(d) === 0 ? 0 : this.contentSize.width)
    else ylocations = d => this.axes.x.func.scale()(yDimNames[yDims.indexOf(d)])

    if (xDims.length > 0) {
      this.content.attr('transform', `translate(${this.margin.left},${this.margin.top})`)
      let topOffset =
        (this.config.horizontal &&
          this.config.AxisHelper[this._getAxisOrder()[0]].side === 'left') ||
        this.config.AxisHelper[this._getAxisOrder()[0]].side === 'top'
          ? this.margin.top
          : this.contentSize.height + this.margin.top
      this.axes[this._getAxisOrder()[0]].selection.attr(
        'transform',
        `translate(${this.margin.left},${topOffset})`
      )
      this._getAxisOrder()
        .slice(1)
        .forEach(dim => {
          let leftOffset =
            (this.config.horizontal && this.config.AxisHelper[dim[0]].side === 'bottom') ||
            this.config.AxisHelper[dim[0]].side === 'left'
              ? this.margin.left
              : this.contentSize.width + this.margin.left
          this.axes[dim].selection.attr(
            'transform',
            `translate(${leftOffset + ylocations(dim)},${this.margin.top})`
          )
        })
    } else {
      this.content.attr(
        'transform',
        `translate(${this.margin.left + this.contentSize.width / 2},${this.margin.top +
          this.contentSize.height / 2})`
      )
      this._getAxisOrder().forEach((dim, i) => {
        this.axes[dim].selection.attr(
          'transform',
          `translate(${this.margin.left + this.contentSize.width / 2},${this.margin.top +
            this.contentSize.height / 2}) rotate( ${180 + (i / yDims.length) * 360} )`
        )
      })
    }
  }
  static _drawAxes = function(axes) {
    let self = this
    let sel, enter
    sel = d3_select(this.$root)
      .selectAll(`.axis`)
      .data(axes)
    enter = sel
      .enter()
      .append('g')
      .attr('class', axis => `axis ${axis}-axis`)
      .style('font-family', 'oecd-microtext-embedded')
      .each(function(axis) {
        d3_select(this).call(self.axes[axis].func)
      })

    sel.exit().remove()

    sel = sel.selection().merge(enter)
    // .style('font-size', `${this.config.AxisHelper.fontSize || 14}px`)

    sel.each(function(axis) {
      let axisSel = d3_select(this)
      self.axes[axis].selection = axisSel
      self._drawAxis(axis, axisSel)
    })
  }
  static _drawTicks = function(axis, enter, update) {
    let self = this

    // TODO:
    // ver como se necesita realmente "todo esto" poruqe hay cosas duplicadas!! (actula_tick_data) por ejemplo
    let isVertical = this._getAxisOrder().indexOf(axis) != 0
    let dims = this['get' + axis[0].toUpperCase() + 'AxesIds']()
    // let shouldDrawGrid = self.config.AxisHelper[axis[0]].grid && (dims.length===1 || dims.indexOf(axis)===0)
    let shouldDrawGrid =
      self.config.AxisHelper[axis[0]].grid &&
      (dims.length === 1 || (dims.length == 2 && dims.indexOf(axis) === 0))

    let func = this.axes[axis].func
    let actual_tick_data =
      func.tickValues() == null
        ? func.scale().ticks
          ? func.scale().ticks.apply(func.scale(), func.tickArguments())
          : func.scale().domain()
        : func.tickValues()
    // let unit = this.config.AxisHelper[axis[0]].unit

    let width = Math.abs(self.axes[axis].scale.range()[1] - self.axes[axis].scale.range()[0])
    let tickSpace = width / actual_tick_data.length
    let tickFontSize
    if (tickSpace < 35) {
      tickFontSize = Math.max(11,Math.floor(tickSpace * 0.35))
    } else {
      tickFontSize = this.config.AxisHelper.fontSize || (this.config.isMobile ? 11 : 12)
    }
    update.style('font-size', `${tickFontSize}px`)
    let unit = this.getUnit(axis)
    let isLastTick = d => {
      return (
        actual_tick_data.findIndex(dd => String(dd) === String(d)) === actual_tick_data.length - 1
      )
    }

    let showUnit = self.getUnit(axis) && self.axes[axis].type === 'left'
    let gUnit = d3_select(this.$root)
      .selectAll('.unit-group')
      .data(showUnit ? [null] : [])
    let gUnitEnter = gUnit
      .enter()
      .append('g')
      .attr('class', 'unit-group')
    gUnit.exit().remove()
    gUnitEnter
      .append('text')
      .attr('class', 'unit')
      .attr('fill', 'currentColor')
      .attr('text-anchor', 'start')
      //.attr('font-weight','bold')
      .text(self.getUnit(axis))
    gUnit = gUnit.merge(gUnitEnter)

    // if(self.getUnit(axis) && self.axes[axis].type === 'left' ) {
    //   d3_select(this.$root).selectAll('.unit-group').remove()
    //   gUnit = d3_select(this.$root).append('g')
    //     .attr('class','unit-group')
    //   gUnit.append('text')
    //     .attr('class','unit')
    //     .attr('fill', 'currentColor')
    //     .attr('text-anchor', 'end')
    //     .text(self.getUnit(axis))

    // }

    // let ticksToDraw = enter //this.__canSkip ? enter : update
    // TODO:
    // QUIZAS HAY QUE "sacar fuera" algunas cosas que hay que hacer SIEMRPE (enter y update)
    // Lo "demas" solo se hace para los enter o para todos en funcion del "__canSkip"

    // CREO QEU LO DE ESCONDER UN TICK I SUSTITUIRLO POR EL TICK CON VALUE*UNIT ERA UNA CHAPUZA porque no tenimaso el "renderTICKS"
    // ALLIN => nos lo cargamos todo y a ver que pasa :D !
    update.each(function(d) {
      // let hasUnit = self.config.AxisHelper[axis[0]].unit && isLastTick(d)
      let hasUnit = self.getUnit(axis) && isLastTick(d)
      let tick = d3_select(this)
      let unitSel = tick.selectAll('.unit').data(hasUnit ? [unit] : [])
      unitSel.exit().remove()
      let unitSelEnter = unitSel
        .enter()
        .append('text')
        .attr('fill', 'currentColor')
        .attr('class', 'unit')
      // unitGruopEnter.append('text').attr('fill', 'currentColor')
      unitSel = unitSel.merge(unitSelEnter)
      unitSel.text(unit)

      tick
        .selectAll('.ghost-unit')
        .data([null])
        .enter()
        .append('text')
        .attr('class', 'ghost-unit')
        .style('opacity', 0)
      let unitGhost = tick.select('.ghost-unit')
      unitGhost.text(unit).attr('font-size', tickFontSize)

      tick
        .selectAll('.ghost-value')
        .data([null])
        .enter()
        .append('text')
        .attr('class', 'ghost-value')
        .style('opacity', 0)

      let valueGhost = tick.select('.ghost-value')
      valueGhost.text(self.axes[axis].func.tickFormat()(d)).attr('font-size', tickFontSize)
      // unitSel.text(formatNumber(d,self.config.valueFormat)+' '+unit)

      let valueSel = tick.select('text')

      if (self.axes.x) {
        valueSel.style('opacity', 1).text(self.axes[axis].func.tickFormat()(d))
      } else {
        valueSel.style('opacity', 0)
      }
      let unitWidth = 0
      let valueWidth = 0
      if (hasUnit) {
        let ghostNode = unitGhost.node()
        if (
          !ghostNode.__measured_width__ ||
          ghostNode.__measured_text__ !== unit ||
          ghostNode.__measured_fontsize__ !== tickFontSize
        ) {
          unitWidth = ghostNode.getComputedTextLength()
          valueWidth = valueGhost.node().getComputedTextLength()
          ghostNode.__measured_width__ = unitWidth
          ghostNode.__measured_value_width = valueWidth
          ghostNode.__measured_text__ = unit
          ghostNode.__measured_fontsize__ = tickFontSize
        } else {
          unitWidth = ghostNode.__measured_width__
          valueWidth = ghostNode.__measured_value_width
        }
      }

      // let valueWidth = valueSel.node().getComputedTextLength()
      // console.log("enter | measured valueWidth",unitWidth)

      if (dims.length < 3 || !self.axes.x) tick.select('line').attr('display', 'none')
      else {
        if (!hasUnit)
          tick
            .select('line')
            .attr('display', 'block')
            .attr('transform', () =>
              self.axes[axis].type === 'left' ? 'translate(2.5,0)' : 'translate(-2.5,0)'
            )
        else tick.select('line').attr('display', 'none')
      }
      if (!self.config.AxisHelper[axis[0]].showTickValues)
        tick.selectAll('text,.unit').attr('display', 'none')
      else tick.selectAll('text,.unit').attr('display', 'inherit')

      let x, y, dy, anchor, spacing, halfheight, fullheight, transform, alignBaseline
      spacing = self.axes[axis].func.tickSize()
      halfheight = '0.36em'
      fullheight = '0.71em'
      transform = null

      // SETTING Xs AND Ys 1
      if (self.axes[axis].type === 'left') {
        x = -spacing
        y = 0
        anchor = 'end'
        if (self.config.AxisHelper[axis[0]].unit) dy = 0
        else dy = halfheight
      } else if (self.axes[axis].type === 'right') {
        x = spacing
        y = 0
        anchor = 'start'
        if (self.config.AxisHelper[axis[0]].unit) dy = 0
        else dy = halfheight
      } else if (self.axes[axis].type === 'bottom') {
        x = 0
        y = 3 + spacing
        anchor = 'middle'
        dy = fullheight
      } else if (self.axes[axis].type === 'top') {
        x = 0
        y = -spacing - 3
        anchor = 'middle'
        dy = fullheight
      }

      if (self.axes[axis].type === 'left') {
        unitSel.remove()
        gUnit.select('text').attr('font-size', tickFontSize)
        gUnit.attr(
          'transform',
          `translate(${self.margin.left - 2*self.axes[axis].func.tickSize() - valueWidth},${tickFontSize})`
        )
        // gUnit && gUnit
        //   .selectAll('text')
        //   .attr('x', self.margin.left - valueWidth - spacing)
        //   .attr('y',tickFontSize/2)
        // .attr('dx', -valueWidth - 3 - self.axes[axis].func.tickSize())
      } else if (self.axes[axis].type === 'right') {
        unitSel.attr('text-anchor', 'start')
        // .attr('dx', -unitWidth + valueWidth + 3 + self.axes[axis].func.tickSize())
      } else if (self.axes[axis].type === 'bottom') {
        unitSel
          .attr('text-anchor', 'end')
          .attr('dy', dy)
          .attr('y', +y + +1.5*tickFontSize)
          .attr('x', x + valueWidth / 2 + 15)
      }

      if (self._getTickOrientationOnAxis(axis) === 'skewed') {
        if (self.axes[axis].type === 'top' || self.axes[axis].type === 'bottom') {
          if (self.axes[axis].type === 'bottom') {
            transform = 'rotate(-45)'
            anchor = 'end'
            y = 0
            x = -3 - spacing
            alignBaseline = 'middle'
          } else {
            transform = 'rotate(-45)'
            anchor = 'start'
            y = 0
            x = 3 + spacing
            alignBaseline = 'middle'
          }
        } else {
          if (self.axes[axis].type === 'left') {
            transform = 'rotate(-45)'
            anchor = 'end'
            y = '-' + fullheight
            x = -3
          } else {
            transform = 'rotate(45)'
            anchor = 'start'
            y = '-' + fullheight
            x = 3
          }
        }
      } else if (self._getTickOrientationOnAxis(axis) === 'vertical') {
        if (self.axes[axis].type === 'top' || self.axes[axis].type === 'bottom') {
          if (self.axes[axis].type === 'bottom') {
            transform = 'rotate(-90)'
            anchor = 'end'
            x = -3 - spacing
            y = 0 //'-' + halfheight
            dy = 0
            alignBaseline = 'middle'
          } else if (self.axes[axis].type === 'top') {
            transform = 'rotate(-90)'
            anchor = 'start'
            x = 3 + spacing
            y = 0 //'-' + halfheight
            dy = 0
            alignBaseline = 'middle'
          }
        } else {
          if (self.axes[axis].type === 'left') {
            transform = 'rotate(-90)'
            anchor = 'end'
            x = 0
            y = -3 - 2 * spacing
            anchor = 'middle'
          } else {
            transform = 'rotate(90)'
            anchor = 'end'
            x = 0
            y = -3 - 2 * spacing
            anchor = 'middle'
          }
        }
      }

      // console.log(x,y,dy,anchor,transform)
      if (self._zooming) tick.interrupt('axis-transition')

      tick
        .select('text')
        .attr('text-anchor', anchor)
        .attr('transform', transform)
        .attr('alignment-baseline', alignBaseline)
        .attr('x', x)
        .attr('y', y)
        .attr('dy', dy)
      //Wrap/ellipse tick texts

      // TODO: hay que calcular este 40 con la distancia entre ticks...
      let maxWidth
      if (!isVertical) {
        if (self.axes[axis].scale.bandwidth) {
          maxWidth = self.axes[axis].scale.bandwidth()
          //excepcion para el "pointScale" del parallels, qeu devuelve siempre bandWidth=0
          if (maxWidth === 0)
            maxWidth =
              (self.axes[axis].scale.range()[1] - self.axes[axis].scale.range()[0]) /
              self.axes[axis].scale.domain().length
        } else {
          // maxWidth = 40
          maxWidth =
            (self.axes[axis].scale.range()[1] - self.axes[axis].scale.range()[0]) /
            actual_tick_data.length
        }
      } else {
        maxWidth = 120
      }
      let type = self.axes[axis].type
      maxWidth = self.config.AxisHelper[axis[0]].customWrapMax || maxWidth
      if (self.config.AxisHelper[axis[0]].tickWrap === 'wrap') {
        tick
          .select('text')
          // .interrupt('axis-transition')
          .call(wrapSvgText, maxWidth)
        //los ".unit" tambien se deben "wrappear" pero solo si son bottom (o top...)
        // if (type === 'bottom' || type === 'top')
        //   unitSel.interrupt('axis-transition').call(wrapSvgText, maxWidth)
      } else if (self.config.AxisHelper[axis[0]].tickWrap === 'ellipsis') {
        tick
          .select('text')
          // .interrupt('axis-transition')
          .call(ellipseSvgText, maxWidth)
        //los ".unit" tambien se deben "ellipsar" pero solo si son bottom (o top...)
        if (type === 'bottom' || type === 'top')
          unitSel.interrupt('axis-transition').call(wrapSvgText, maxWidth)
      }

      let gridLine = tick.selectAll('.grid-line').data(shouldDrawGrid ? [null] : [])
      gridLine.exit().remove()
      gridLine = gridLine.merge(
        gridLine
          .enter()
          .append('line')
          .attr('class', 'grid-line')
      )

      let gridColors =
        self.config.skin === 'light' ? ['#e3e3e3', '#e3e3e3'] : ['#434343', '#434343']

      // FOR TESTING!!!
      // let gridColors =
      //   self.config.skin === 'light' ? ['#000', '#000'] : ['#000', '#000']

      let isSolidZero = false //Force removo solid zero d === 0 && self.axes[axis].func.scale().domain()[0] < 0
      gridLine
        .attr('display', 'inherit')
        .attr('stroke', isSolidZero ? gridColors[0] : gridColors[1])
        .attr('shape-rendering', 'crispedges')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '1 3') //remove solid zero

      if (!isVertical) {
        let sign = self.config.AxisHelper[axis[0]].side === 'top' ? 1 : -1
        gridLine
          .attr('y1', sign * self.contentSize.height)
          .attr('x1', 0)
          .attr('x2', 0)
      } else {
        let sign = self.config.AxisHelper[axis[0]].side === 'left' ? 1 : -1
        gridLine
          .attr('x1', sign * self.contentSize.width)
          .attr('x2', -self.axes[axis].func.tickSize() / 2)
          //.attr('x2', sign * ((hasUnit ? unitWidth + 10 : 0) - self.axes[axis].func.tickSize() / 2))
          .attr('y1', 0)
      }
    })

    // update.each(function(d){
    //   let hasUnit = self.getUnit(axis) && isLastTick(d)
    //   let tick = d3_select(this)
    //   let hadUnit = tick.attr('__had_unit__')
    //   if(!hasUnit && !hadUnit) return
    //   if(hasUnit) tick.attr('__had_unit__',true)
    //   let unitGroup = tick.selectAll('.unit').data(hasUnit ? [unit] : [])
    //   unitGroup.exit().remove()
    //   let unitGruopEnter = unitGroup
    //     .enter()
    //     .append('g')
    //     .attr('class', 'unit')
    //   unitGruopEnter.append('text').attr('fill', 'currentColor')
    //   unitGroup = unitGroup.merge(unitGruopEnter)
    //   let unitSel = unitGroup.select('text')
    //   // unitSel.text(formatNumber(d,self.config.valueFormat)+' '+unit)
    //   unitSel.text(self.valueWithUnit.call(self, d, axis))
    //   let valueSel = tick.select('text')
    //   if (self.axes.x) {
    //     valueSel.style('opacity', hasUnit ? '0' : '1').text(self.axes[axis].func.tickFormat()(d))
    //   } else {
    //     valueSel.style('opacity', 0)
    //   }
    //   let unitWidth = hasUnit ? unitSel.node().getComputedTextLength() : 0
    //   let valueWidth = valueSel.node().getComputedTextLength()
    //   console.log("update | measured valueWidth",valueWidth)

    //   if (self.axes[axis].type === 'left') {
    //     x = -spacing
    //     y = 0
    //     anchor = 'end'
    //     if (self.config.AxisHelper[axis[0]].unit) dy = 0
    //     else dy = halfheight
    //   } else if (self.axes[axis].type === 'right') {
    //     x = spacing
    //     y = 0
    //     anchor = 'start'
    //     if (self.config.AxisHelper[axis[0]].unit) dy = 0
    //     else dy = halfheight
    //   } else if (self.axes[axis].type === 'bottom') {
    //     x = 0
    //     y = 3 + spacing
    //     anchor = 'middle'
    //     dy = fullheight
    //   } else if (self.axes[axis].type === 'top') {
    //     x = 0
    //     y = -spacing - 3
    //     anchor = 'middle'
    //     dy = fullheight
    //   }

    //   let x, y, dy, anchor, spacing, halfheight, fullheight, transform
    //   if (self.axes[axis].type === 'left') {
    //     unitSel
    //       .attr('text-anchor', 'start')
    //       .attr('dx', -valueWidth - 3 - self.axes[axis].func.tickSize())
    //   } else if (self.axes[axis].type === 'right') {
    //     unitSel
    //       .attr('text-anchor', 'start')
    //       .attr('dx', -unitWidth + valueWidth + 3 + self.axes[axis].func.tickSize())
    //   } else if (self.axes[axis].type === 'bottom') {
    //     unitSel
    //       .attr('text-anchor', anchor)
    //       .attr('dy', dy)
    //       .attr('y', y)
    //       .attr('x', x)
    //   }
    //   let gridLine = tick.selectAll('.grid-line')

    //   if (!isVertical) {
    //     let sign = self.config.AxisHelper[axis[0]].side === 'top' ? 1 : -1
    //     gridLine
    //       .attr('y1', sign * self.contentSize.height)
    //       .attr('x1', 0)
    //       .attr('x2', 0)
    //   } else {
    //     let sign = self.config.AxisHelper[axis[0]].side === 'left' ? 1 : -1
    //     gridLine
    //       .attr('x1', sign * self.contentSize.width)
    //       .attr(
    //         'x2',
    //         sign * ((hasUnit ? unitWidth - valueWidth : 0) - self.axes[axis].func.tickSize() / 2)
    //       )
    //       .attr('y1', 0)
    //   }
    // })
  }
  static _drawAxis = function(axis, sel) {
    let staticSel = sel
    if (this._firstAxisRender || this._zooming) {
      sel = sel
        .interrupt('axis-transition')
        // .transition('axis-transition')
        // .duration(0)
        .call(this.axes[axis].func)
    } else {
      sel = sel
        .transition('axis-transition')
        .duration(this._transitionDuration)
        .call(this.axes[axis].func)
    }

    let func = this.axes[axis].func
    let actual_tick_data =
      func.tickValues() == null
        ? func.scale().ticks
          ? func.scale().ticks.apply(func.scale(), func.tickArguments())
          : func.scale().domain()
        : func.tickValues()

    let ids = this[`get${axis[0].toUpperCase()}AxesIds`]()
    let index = ids.indexOf(axis)
    let type = this.axes[axis].type
    // Solo aplicamos al primero y ultimo + SI hay eje x + SI se estan mostrando lo values!
    if (!this._zooming || this.zooming) {
      if (
        this.axes.x &&
        (index === 0 || index === ids.length - 1) &&
        this.config.AxisHelper[axis[0]].showTickValues
      ) {
        let usedLength = 0
        sel
          .selectAll('.tick')
          .select('text')
          .each(function(d) {
            if (!actual_tick_data.find(dd => String(dd) === String(d))) return
            let dim = type === 'bottom' || type === 'top' ? 'height' : 'width'
            usedLength = Math.max(usedLength, this.getBoundingClientRect()[dim])
          })
        //los ".unit" tambien deben "empujar" pero solo si son bottom (o top...)
        sel
          .selectAll('.tick')
          .select('.unit')
          .select('text')
          .each(function(d) {
            if (!actual_tick_data.find(dd => String(dd) === String(d))) return
            let dim = type === 'bottom' || type === 'top' ? 'height' : 'width'
            if (type === 'bottom' || type === 'top')
              usedLength = Math.max(usedLength, this.getBoundingClientRect()[dim])
          })

        // Add some "slack"
        usedLength += 10

        if (usedLength > this.margin[type]) {
          this.margin[type] = usedLength
          this.needsRedraw = true
        } else if (usedLength < this.defMargin[type] && this.defMargin[type] < this.margin[type]) {
          this.margin[type] = this.defMargin[type]
          this.needsRedraw = true
        }
      }
    }

    if (this.config.showLineLabel) {
      // BASURA, no se puede usar "getComputedTextLength" porque aun no se ha ejecutado el "draw"...

      // let maxWidth = d3_max(this.parentItems.merge(this.parentItemsEnter).nodes(), el => {
      //   let node = d3_select(el).select('.line-label').select('text').node()
      //   return node ? node.getComputedTextLength() : this.margin.right
      // })
      let maxWidth = d3_max(this.data, d => d.id.length * 8)

      if (this.margin.right < maxWidth) {
        this.needsRedraw = true
        this.margin.right = maxWidth
      }
    }

    let yDims = this.getYAxesIds()
    if (axis === 'x' || yDims.length < 3) sel.select('.domain').attr('display', 'none')

    if (this.axes[axis].func.scale().isChunky) {
      let subRanges = this.axes[axis].func.scale().subRanges()
      let subDoms = this.axes[axis].selection.selectAll('.sub-domain').data(subRanges)
      subDoms = subDoms.merge(
        subDoms
          .enter()
          .append('line')
          .attr('class', 'sub-domain')
      )
      subDoms.attr('stroke', 'currentColor')
      if (type === 'bottom' || type === 'top') {
        subDoms
          .attr('y1', 0)
          .attr('y2', 0)
          .attr('x1', d => d[1])
          .attr('x2', d => d[0])
      } else {
        subDoms
          .attr('x1', 0)
          .attr('x2', 0)
          .attr('y1', d => d[0])
          .attr('y2', d => d[1])
      }
    } else {
      this.axes[axis].selection.selectAll('.sub-domain').remove()
    }
    // this.content.raise()

    // AJustamos los "units" en multi y axes (parallels (y radar))
    if (yDims.length > 2) {
      if (index >= 0 && this.getUnit(axis)) {
        let tickPos = this.axes[axis].func.scale()(actual_tick_data[actual_tick_data.length - 1])
        let groundPos = this.axes[axis].func.scale().range()[0]
        this.axes[axis].selection
          .select('.domain')
          .interrupt('axis-transition')
          .attr('d', `M0.5,${groundPos}V${tickPos + 5}M0.5,${Math.max(0, tickPos - 14)}V0`)
      }
    }
  }
  static _redrawAxes = function() {
    let yDims = this.getYAxesIds()
    let xDims = this.getXAxesIds()
    let axes = [...xDims, ...yDims]
    this._drawAxes(axes)
    this._positionAxes()
  }
  static beforeDraw() {
    this._updateAxes() //includes a _redrawAxes
  }

  static draw() {}

  static afterDraw() {
    // let restrictDomain = (originalDomain, newDomain) => {
    //   let addDiff = (x, a, b) => {
    //     if (x.getTime) return new Date(x.getTime() + (b.getTime() - a.getTime()))
    //     else return x + (b - a)
    //   }
    //   if (newDomain[1] > originalDomain[1]) {
    //     newDomain = newDomain.map(x => addDiff(x, newDomain[1], originalDomain[1]))
    //   }
    //   if (newDomain[0] < originalDomain[0]) {
    //     newDomain = newDomain.map(x => addDiff(x, newDomain[0], originalDomain[0]))
    //   }
    //   console.log("restricted domain",newDomain)
    //   return newDomain
    // }
    let zoomedSemantic = ({ transform }, skipTransitions = true) => {
      transform.x = Math.min(transform.x, -this.axes.x.scale.range()[0] * transform.k)
      transform.x = Math.max(
        transform.x,
        -this.axes.x.scale.range()[1] * transform.k + this.contentSize.width
      )
      transform.y = Math.min(transform.y, -this.axes.y.scale.range()[1] * transform.k)
      transform.y = Math.max(
        transform.y,
        -this.axes.y.scale.range()[0] * transform.k + this.contentSize.height
      )

      const zx = transform.rescaleX(this.axes.x.scale).interpolate(d3_interpolateRound)
      const zy = transform.rescaleY(this.axes.y.scale).interpolate(d3_interpolateRound)
      // let originalDomain = this.axes.x.scale.domain()
      // let newDomain = zx.domain()

      // zx.domain(newDomain)
      // zx.domain(restrictDomain(this.axes.x.scale.domain(), zx.domain()))
      // zy.domain(restrictDomain(this.axes.y.scale.domain(), zy.domain()))
      if (this.config.AxisHelper.zoomAxes.indexOf('x') >= 0) this.axes.x.func.scale(zx)
      if (this.config.AxisHelper.zoomAxes.indexOf('y') >= 0) this.axes.y.func.scale(zy)
      this._zooming = skipTransitions
      this._beforeDraws[this._helperIds.indexOf('ItemsHelper')].call(this)
      // this._beforeDraws[this._helperIds.indexOf('AxisHelper')].call(this)
      this._redrawAxes()
      this.draw()
      this._zooming = false
      // this.emit('translate', {
      //   x: this.axes.x.func.scale().range(),
      //   y: this.axes.y.func.scale().range(),
      // })
    }
    let zoomedGeometric = ({ transform }) => {
      let td = this.config.horizontal ? transform.y : transform.x
      this.axes.x.func.scale().range(this.axes.x.scale.range().map(d => d + td))
      this._zooming = true
      this._beforeDraws[this._helperIds.indexOf('ItemsHelper')].call(this)
      // this._beforeDraws[this._helperIds.indexOf('AxisHelper')].call(this)
      this._redrawAxes()
      this.draw()
      this._zooming = false

      if (this.config.AxisHelper.onZoom) {
        this.config.AxisHelper.onZoom()
      }
      // this.emit('translate', { x: this.axes.x.func.scale().range() })
    }

    let zoomYTarget = this.axes.y || this.axes.y_0
    if (this.config.AxisHelper.zoom && !this.axes.x.scale.bandwidth) {
      const zoom = d3_zoom().scaleExtent([1, 3])
      //PARA EL LINECHART
      if (
        this.config.AxisHelper.zoomAxes.length === 1 &&
        this.config.AxisHelper.zoomAxes[0] === 'x'
      ) {
        let minDist = getMinDist(
          this.data,
          this.axes['x'].scale,
          d =>
            // this._getValueOnAxis(d, 'x')
            d.id
        )
        let initialScale = minDist < 50 ? 50 / minDist : 1
        let initialTransform = d3_zoomIdentity.scale(initialScale)
        d3_select(this.$root)
          .call(zoom)
          .call(zoom.transform, initialTransform)
        this.axes.x.func.ticks(Math.ceil(DEFAULT_TICK_COUNT.x / initialScale))

        if (this.config.AxisHelper.zoomAxes.indexOf('x') >= 0) {
          this.axes.x.func.scale().range(this.axes.x.scale.range().map(d => initialScale * d))
        }
        if (this.config.AxisHelper.zoomAxes.indexOf('y') >= 0) {
          this.axes.x.func.scale().range(this.axes.x.scale.range().map(d => initialScale * d))
        }
        zoomedSemantic({ transform: initialTransform }, false)
      } else {
        //PARA LOS "SCATTER Y CANDELS"
        const zoom = d3_zoom().scaleExtent([1, 5])
        d3_select(this.$root)
          .call(zoom)
          .call(zoom.transform, d3_zoomIdentity)
        zoom.on('zoom', zoomedSemantic)
        d3_select(this.$root).style('cursor', 'move')
        zoomedSemantic({ transform: d3_zoomIdentity }, false)
      }

      // HAY QUE HACERLO (BIEN) Tambien para las "y's" y ver "que pasa en TODOS los casos... (paralels bars areas...)"

      this._redrawAxes()
      this.draw()
      zoom.on('zoom', zoomedSemantic)
      d3_select(this.$root).style('cursor', 'move')
    } else if (this.config.AxisHelper.zoom) {
      //PARA LOS BARS, PARALELS etc
      let sortedXRange = this.config.horizontal
        ? zoomYTarget.scale.range().sort((a, b) => a - b)
        : this.axes.x.scale.range().sort((a, b) => a - b)
      let sortedYRange = this.config.horizontal
        ? this.axes.x.scale.range().sort((a, b) => a - b)
        : zoomYTarget.scale.range().sort((a, b) => a - b)

      // chapuzilla para el "parallels" pero parece OK, no?
      if (this.getYAxesIds().length > 2) {
        sortedXRange = [sortedXRange[0] - 40, sortedXRange[1] + 40]
      }
      const zoom = d3_zoom()
        .scaleExtent([1, 1])
        .translateExtent([
          [sortedXRange[0], sortedYRange[0]],
          [
            sortedXRange[1] + this.margin.left + this.margin.right,
            sortedYRange[1] + this.margin.top + this.margin.bottom,
          ],
        ])
      d3_select(this.$root)
        .call(zoom)
        .call(zoom.transform, d3_zoomIdentity)
      zoom.on('zoom', zoomedGeometric)
      d3_select(this.$root).style('cursor', 'move')
    } else {
      d3_select(this.$root).on('.zoom', null)
      d3_select(this.$root).style('cursor', null)
    }

    if (this.config.AxisHelper.zoom) {
      let clips = this.root.selectAll('clipPath').data(['content', 'x', 'y'])
      let clipsEnter = clips
        .enter()
        .append('clipPath')
        .attr('id', d => 'zoom-clip-' + d + '-' + this._chartId)

      clipsEnter.append('rect')

      clips = clips.merge(clipsEnter)

      this.root
        .select(`#zoom-clip-content-${this._chartId}`)
        .select('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', this.contentSize.width)
        .attr('height', this.contentSize.height)

      let long = this.config.horizontal ? 'x' : 'y'
      let short = this.config.horizontal ? 'y' : 'x'
      this.root
        .select(`#zoom-clip-${short}-${this._chartId}`)
        .select('rect')
        .attr('x', -40)
        .attr('y', -this.contentSize.height)
        .attr('width', this.contentSize.width + 80)
        .attr('height', this.contentSize.height + this.margin.bottom)

      this.root
        .select(`#zoom-clip-${long}-${this._chartId}`)
        .select('rect')
        .attr(
          'x',
          zoomYTarget.type === 'left'
            ? -this.margin.left
            : -this.margin.left - this.contentSize.width
        )
        .attr('y', -10)
        .attr('width', this.contentSize.width + this.margin.left + this.margin.right)
        .attr('height', this.contentSize.height + 20)
      this.content.attr('clip-path', `url(#zoom-clip-content-${this._chartId})`)
      this.axes.x.selection.attr('clip-path', `url(#zoom-clip-x-${this._chartId})`)
      zoomYTarget.selection.attr('clip-path', `url(#zoom-clip-y-${this._chartId})`)
    } else {
      this.content.attr('clip-path', null)
      if (this.axes.x) this.axes.x.selection.attr('clip-path', null)
      if (zoomYTarget) zoomYTarget.selection.attr('clip-path', null)
    }
  }
}
